import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../utilitarios/cn'
import { Botao } from './botao'

const LARGURAS = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-xl',
} as const

export interface PropriedadesGaveta {
  aberta: boolean
  onFechar: () => void
  titulo: ReactNode
  descricao?: ReactNode
  children: ReactNode
  rodape?: ReactNode
  largura?: keyof typeof LARGURAS
  lado?: 'direita' | 'esquerda'
}

/**
 * Painel lateral — o "Filtrar Leads" da TELA-04, o menu de navegação no celular e
 * o resumo de evento do calendário.
 *
 * Gaveta e não modal quando o conteúdo é **auxiliar** e o usuário precisa
 * enxergar o contexto de trás (a lista que os filtros vão recortar). Modal quando
 * a decisão é bloqueante.
 */
export function Gaveta({
  aberta,
  onFechar,
  titulo,
  descricao,
  children,
  rodape,
  largura = 'md',
  lado = 'direita',
}: PropriedadesGaveta) {
  const painel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberta) return
    const anterior = document.activeElement as HTMLElement | null
    painel.current?.focus()

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') onFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.body.style.overflow = ''
      anterior?.focus?.()
    }
  }, [aberta, onFechar])

  if (!aberta) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        onClick={onFechar}
        className="absolute inset-0 bg-grafite-950/50 backdrop-blur-[2px]"
      />
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={cn(
          'absolute inset-y-0 flex w-full flex-col bg-superficie shadow-lg outline-none',
          LARGURAS[largura],
          lado === 'direita' ? 'right-0' : 'left-0',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-borda px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-conteudo">{titulo}</h2>
            {descricao ? <p className="mt-1 text-sm text-conteudo-suave">{descricao}</p> : null}
          </div>
          <Botao
            variante="fantasma"
            tamanho="icone"
            onClick={onFechar}
            aria-label="Fechar"
            className="-mr-2 -mt-1"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor">
              <path d="M6.3 5a.9.9 0 0 0-1.3 1.3L8.7 10 5 13.7A.9.9 0 0 0 6.3 15L10 11.3 13.7 15a.9.9 0 0 0 1.3-1.3L11.3 10 15 6.3A.9.9 0 0 0 13.7 5L10 8.7 6.3 5Z" />
            </svg>
          </Botao>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {rodape ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-borda bg-superficie-2 px-5 py-3">
            {rodape}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
